import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Languages, Check, Zap, Clock, Loader2, Copy, ExternalLink, Wallet, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Stablecoin configuration
const STABLECOINS = {
  USDC: {
    name: "USDC",
    mint: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", // Official USDC mint on Solana
    decimals: 6,
    color: "blue"
  },
  USDT: {
    name: "USDT", 
    mint: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB", // Official USDT mint on Solana
    decimals: 6,
    color: "green"
  }
};

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [stablecoinInfo, setStablecoinInfo] = useState(null);
  const [selectedStablecoin, setSelectedStablecoin] = useState("USDC");
  const [txSignature, setTxSignature] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  
  // Fetch plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/payments/plans`);
        setPlans(response.data.plans);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };
    fetchPlans();
  }, []);
  
  // Fetch stablecoin payment info
  const fetchStablecoinInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/stablecoin-info?t=${Date.now()}`);
      setStablecoinInfo(response.data);
    } catch (error) {
      console.error("Error fetching stablecoin info:", error);
      // Fallback to default values
      setStablecoinInfo({
        price_usd: 8.99,
        wallet_address: "2HyWUVk42Q2EQVfdguQvh9YV6AFVsTFBYXdo9dRwYFBb"
      });
    }
  };
  
  useEffect(() => {
    fetchStablecoinInfo();
  }, []);
  
  // Stripe Payment Link
  const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/6oUdR27M43tl5X92Nn9AA00";
  
  // Handle Stripe checkout - redirect to Stripe Payment Link
  const handleStripeCheckout = async () => {
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }
    
    setLoading(true);
    setMessage({ type: "", text: "" });
    
    try {
      // Build the Stripe Payment Link URL with user email prefilled
      let paymentUrl = STRIPE_PAYMENT_LINK;
      
      // Add email as prefilled parameter if user is authenticated
      if (user?.email) {
        paymentUrl += `?prefilled_email=${encodeURIComponent(user.email)}`;
      }
      
      // Redirect to Stripe Payment Link
      window.location.href = paymentUrl;
    } catch (error) {
      setMessage({ type: "error", text: "Erro ao redirecionar para pagamento" });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Solana payment verification
  const handleSolanaVerify = async () => {
    if (!txSignature.trim()) {
      setMessage({ type: "error", text: t("errorEnterTxSignature") });
      return;
    }
    
    setVerifying(true);
    setMessage({ type: "", text: "" });
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/solana/verify`,
        { tx_signature: txSignature },
        { withCredentials: true }
      );
      
      setMessage({ type: "success", text: response.data.message });
      await refreshUser();
      
      // Redirect to dashboard after success
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t("errorVerifyTx");
      setMessage({ type: "error", text: errorMsg });
    } finally {
      setVerifying(false);
    }
  };
  
  // Copy wallet address
  const copyWalletAddress = () => {
    if (stablecoinInfo?.wallet_address) {
      navigator.clipboard.writeText(stablecoinInfo.wallet_address);
      setMessage({ type: "success", text: t("addressCopied") });
      setTimeout(() => setMessage({ type: "", text: "" }), 2000);
    }
  };
  
  const isPro = user?.plan === "pro";
  
  return (
    <div className="min-h-screen bg-zinc-950 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <Languages className="w-10 h-10 text-white" />
            <span className="text-2xl font-bold text-white font-['Outfit']">
              {t("appTitle")}
            </span>
          </Link>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {t("pricingTitle")}
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            {t("pricingSubtitle")}
          </p>
        </div>
        
        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
          {/* Free Plan */}
          <Card className={`bg-zinc-900/50 border-zinc-800 ${user?.plan === "free" ? "border-green-500/50" : ""}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-xl">{t("planFree")}</CardTitle>
                {user?.plan === "free" && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                    {t("currentPlan")}
                  </span>
                )}
              </div>
              <CardDescription className="text-zinc-400">
                {t("perfectToTry")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <span className="text-4xl font-bold text-white">{t("freePrice")}</span>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-zinc-300">
                  <Clock className="w-4 h-4 text-zinc-500" />
                  {t("feature10Minutes")}
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("featureBidirectional")}
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("feature20Languages")}
                </li>
              </ul>
              
              {!isAuthenticated && (
                <Button
                  onClick={() => navigate("/auth")}
                  variant="outline"
                  className="w-full border-zinc-700 hover:bg-zinc-800 text-white"
                >
                  {t("startFree")}
                </Button>
              )}
            </CardContent>
          </Card>
          
          {/* PRO Plan */}
          <Card className={`bg-gradient-to-br from-zinc-900/80 to-zinc-800/50 border-2 ${isPro ? "border-yellow-500/50" : "border-white/20"}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-xl flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  {t("planPro")}
                </CardTitle>
                {isPro && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                    {t("currentPlan")}
                  </span>
                )}
              </div>
              <CardDescription className="text-zinc-400">
                {t("forProfessionalUse")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <span className="text-4xl font-bold text-white">$8.99</span>
                <span className="text-zinc-400">{t("perMonth")}</span>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-yellow-500" />
                  <span className="font-medium">{t("featureUnlimited")}</span>
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("featureBidirectional")}
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("feature20Languages")}
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("feature2Screens")}
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("featureOBS")}
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("featureQRCode")}
                </li>
              </ul>
              
              {isPro ? (
                <Button
                  disabled
                  className="w-full bg-yellow-500/20 text-yellow-400 cursor-not-allowed"
                >
                  {t("alreadyPro")}
                </Button>
              ) : (
                <Tabs defaultValue="stripe" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-zinc-800">
                    <TabsTrigger value="stripe" className="data-[state=active]:bg-zinc-700">
                      {t("creditCard")}
                    </TabsTrigger>
                    <TabsTrigger value="solana" className="data-[state=active]:bg-zinc-700">
                      Solana
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="stripe" className="mt-4">
                    <Button
                      onClick={handleStripeCheckout}
                      disabled={loading}
                      className="w-full bg-white text-black hover:bg-zinc-200"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      {t("payWithCard")}
                    </Button>
                  </TabsContent>
                  
                  <TabsContent value="solana" className="mt-4 space-y-4">
                    {!isAuthenticated ? (
                      <Button
                        onClick={() => navigate("/auth")}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {t("loginToPay")}
                      </Button>
                    ) : (
                      <>
                        {/* Stablecoin Selection */}
                        <div className="flex gap-2 mb-2">
                          <button
                            onClick={() => setSelectedStablecoin("USDC")}
                            className={`flex-1 py-2 px-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                              selectedStablecoin === "USDC"
                                ? "bg-blue-500/20 border-blue-500 text-blue-400"
                                : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600"
                            }`}
                          >
                            <DollarSign className="w-4 h-4" />
                            USDC
                          </button>
                          <button
                            onClick={() => setSelectedStablecoin("USDT")}
                            className={`flex-1 py-2 px-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                              selectedStablecoin === "USDT"
                                ? "bg-green-500/20 border-green-500 text-green-400"
                                : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600"
                            }`}
                          >
                            <DollarSign className="w-4 h-4" />
                            USDT
                          </button>
                        </div>

                        {/* Stablecoin Payment Info */}
                        {stablecoinInfo && (
                          <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-zinc-400">{t("amount")}:</span>
                              <span className="text-white font-bold text-lg">
                                ${stablecoinInfo.price_usd?.toFixed(2) || "8.99"} {selectedStablecoin}
                              </span>
                            </div>
                            
                            <div className="text-xs text-zinc-500 bg-zinc-900/50 p-2 rounded">
                              {t("noConversionNeeded")} {selectedStablecoin} ({t("solanaNetwork")})
                            </div>
                            
                            <div className="pt-2 border-t border-zinc-700">
                              <Label className="text-zinc-400 text-xs">{t("sendTo")} {selectedStablecoin}:</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <code className="flex-1 bg-zinc-900 px-2 py-1 rounded text-xs text-zinc-300 truncate">
                                  {stablecoinInfo.wallet_address}
                                </code>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={copyWalletAddress}
                                  className="h-8 w-8 p-0"
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            
                            <div className="text-xs text-zinc-500">
                              Token: {selectedStablecoin === "USDC" ? "USDC (SPL)" : "USDT (SPL)"} {t("tokenOnSolana")}
                            </div>
                          </div>
                        )}
                        
                        {/* Transaction Verification */}
                        <div className="space-y-2">
                          <Label className="text-zinc-300 text-sm">
                            {t("txSignatureLabel")}
                          </Label>
                          <Input
                            placeholder={t("txSignaturePlaceholder")}
                            value={txSignature}
                            onChange={(e) => setTxSignature(e.target.value)}
                            className="bg-zinc-800 border-zinc-700 text-white text-xs font-mono"
                          />
                        </div>
                        
                        <Button
                          onClick={handleSolanaVerify}
                          disabled={verifying || !txSignature.trim()}
                          className="w-full bg-purple-600 hover:bg-purple-700"
                        >
                          {verifying ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Wallet className="w-4 h-4 mr-2" />
                          )}
                          {t("verifyPayment")}
                        </Button>
                        
                        <a
                          href="https://solscan.io"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
                        >
                          {t("viewOnSolscan")} <ExternalLink className="w-3 h-3" />
                        </a>
                      </>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Message */}
        {message.text && (
          <div className={`max-w-md mx-auto mb-8 p-4 rounded-lg text-center ${
            message.type === "error" 
              ? "bg-red-500/20 text-red-400" 
              : "bg-green-500/20 text-green-400"
          }`}>
            {message.text}
          </div>
        )}
        
        {/* Back link */}
        <div className="text-center">
          <Link to="/dashboard" className="text-zinc-500 hover:text-zinc-300 text-sm">
            ← {t("backToDashboard")}
          </Link>
        </div>
        
        {/* Copyright */}
        <p className="mt-12 text-zinc-700 text-[10px] sm:text-xs text-center">
          {t("footerCopyright")}
        </p>
      </div>
    </div>
  );
}
